import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import {
  ChevronLeft, ChevronRight, Download, Waves, Bike, Footprints, Dumbbell,
  Clock, BarChart3, ChevronDown, ChevronUp, Star
} from "lucide-react";
import axios from "axios";
import LoadingScreen from "@/components/LoadingScreen";
import VolumeChart from "@/components/VolumeChart";
import { SPORT_COLORS, DAY_NAMES } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS_ORDER = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_LABELS = { Mon: "T2", Tue: "T3", Wed: "T4", Thu: "T5", Fri: "T6", Sat: "T7", Sun: "CN" };

const SportIcon = ({ sport, className = "w-4 h-4" }) => {
  const icons = { swim: Waves, bike: Bike, run: Footprints, strength: Dumbbell };
  const Icon = icons[sport] || Dumbbell;
  return <Icon className={className} />;
};

const ExerciseCard = ({ exercise, onClick }) => {
  const colors = SPORT_COLORS[exercise.exercise_sport_type] || SPORT_COLORS.run;
  const sportLabel = { swim: "Bơi", bike: "Đạp", run: "Chạy", strength: "Bổ trợ" };
  return (
    <div
      className={`workout-card relative ${colors.border}`}
      onClick={onClick}
      data-testid={`exercise-${exercise.exercise_sport_type}-${exercise.exercise_name?.slice(0, 10)}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${colors.badge}`}>
          <SportIcon sport={exercise.exercise_sport_type} className="w-3 h-3" />
          {sportLabel[exercise.exercise_sport_type] || exercise.exercise_sport_type}
        </span>
        {exercise.exercise_label && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">{exercise.exercise_label}</Badge>
        )}
      </div>
      <p className="text-sm font-semibold text-slate-800 leading-snug break-words overflow-hidden">
        {exercise.exercise_name}
      </p>
      <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 flex-wrap">
        <Clock className="w-3 h-3" />
        <span>{exercise.exercise_time}</span>
      </div>
    </div>
  );
};

const DayColumn = ({ dayName, exercises, onExerciseClick }) => {
  const hasExercises = exercises.length > 0;
  return (
    <div className={`p-3 ${hasExercises ? "bg-white" : "bg-slate-50"} min-h-[100px] rounded-lg overflow-hidden`}>
      <div className="flex justify-between items-center mb-2">
        <span className="text-xs font-bold text-slate-400 uppercase">{DAY_LABELS[dayName] || dayName}</span>
      </div>
      <div className="space-y-2">
        {exercises.map((ex, i) => (
          <ExerciseCard key={i} exercise={ex} onClick={() => onExerciseClick(ex)} />
        ))}
        {!hasExercises && <div className="text-xs text-slate-400 italic">Nghỉ</div>}
      </div>
    </div>
  );
};

export default function PlanPage() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState(0);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [showChart, setShowChart] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    let interval;
    const fetchPlan = async () => {
      try {
        const res = await axios.get(`${API}/plans/${planId}`);
        const data = res.data;
        if (data.status === "ready") {
          setPlan(data);
          setLoading(false);
          if (interval) clearInterval(interval);
        } else if (data.status === "error") {
          setLoading(false);
          setPlan({ error: true });
          if (interval) clearInterval(interval);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchPlan();
    interval = setInterval(fetchPlan, 2500);
    return () => clearInterval(interval);
  }, [planId]);

  const handleExerciseClick = (exercise) => {
    setSelectedExercise(exercise);
    setSheetOpen(true);
  };

  const handlePrint = () => window.print();

  // Group exercises by day for the selected week
  const exercisesByDay = useMemo(() => {
    if (!plan?.weeks?.[selectedWeek]) return {};
    const grouped = {};
    for (const day of DAYS_ORDER) grouped[day] = [];
    for (const ex of plan.weeks[selectedWeek].exercises || []) {
      const day = ex.exercise_date || "Mon";
      if (!grouped[day]) grouped[day] = [];
      grouped[day].push(ex);
    }
    return grouped;
  }, [plan, selectedWeek]);

  // Derive unique phases from weeks
  const phaseNames = useMemo(() => {
    if (!plan?.weeks) return [];
    const seen = new Set();
    return plan.weeks.reduce((acc, w) => {
      const pack = (w.exercises?.[0]?.exercise_pack) || "Plan";
      if (!seen.has(pack)) { seen.add(pack); acc.push(pack); }
      return acc;
    }, []);
  }, [plan]);

  // Build volume chart data from new schema
  const chartWeeks = useMemo(() => {
    if (!plan?.weeks) return [];
    return plan.weeks.map((w, i) => ({
      week: i + 1,
      phase: 1,
      total_hours: 0,
      days: DAYS_ORDER.map(day => ({
        workouts: (w.exercises || [])
          .filter(ex => ex.exercise_date === day)
          .map(ex => ({
            sport: ex.exercise_sport_type,
            duration_min: _parseTime(ex.exercise_time),
          }))
      }))
    }));
  }, [plan]);

  if (loading) return <LoadingScreen />;

  if (!plan || plan.error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <h2 className="text-xl font-bold text-slate-800 mb-2">Có lỗi xảy ra</h2>
          <p className="text-slate-500 mb-4">Không thể tạo plan. Vui lòng thử lại.</p>
          <Button onClick={() => navigate("/")} data-testid="retry-btn">Quay lại</Button>
        </div>
      </div>
    );
  }

  const weeks = plan.weeks || [];
  const currentWeek = weeks[selectedWeek];
  const config = plan.config || {};
  const currentPack = currentWeek?.exercises?.[0]?.exercise_pack || "";

  return (
    <div className="min-h-screen bg-slate-50" ref={printRef}>
      {/* Header */}
      <header className="bg-slate-900 text-white py-4 px-4 sticky top-0 z-30 no-print">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")} className="text-white hover:text-orange-400" data-testid="back-home-btn">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-black uppercase tracking-tight">
                <span className="text-orange-400">BĐC</span> {plan.training_plan_name || "Plan"}
              </h1>
              <span className="text-slate-400 text-xs">
                {config.training_duration} tuần – {plan.total_exercise || 0} bài tập – {plan.total_training_plan_time || ""}
              </span>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={handlePrint} className="text-white hover:text-orange-400" data-testid="export-pdf-btn">
            <Download className="w-5 h-5" />
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-4">
        {/* Phase indicator */}
        <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2 no-print">
          {phaseNames.map((name, i) => (
            <div key={i} className="flex items-center gap-2 flex-shrink-0">
              <div
                className={`px-3 py-1.5 rounded-full text-xs font-bold cursor-pointer transition-all ${
                  currentPack === name ? "bg-orange-500 text-white" : "bg-slate-200 text-slate-500"
                }`}
                onClick={() => {
                  const idx = weeks.findIndex(w => w.exercises?.[0]?.exercise_pack === name);
                  if (idx >= 0) setSelectedWeek(idx);
                }}
                data-testid={`phase-btn-${i}`}
              >
                {name}
              </div>
              {i < phaseNames.length - 1 && <div className="w-4 h-0.5 bg-slate-300" />}
            </div>
          ))}
        </div>

        {/* Week navigator */}
        <div className="flex items-center justify-between mb-4 no-print">
          <Button variant="ghost" size="sm" onClick={() => setSelectedWeek(Math.max(0, selectedWeek - 1))} disabled={selectedWeek === 0} data-testid="prev-week-btn">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="text-center">
            <span className="text-lg font-black uppercase">{currentWeek?.week_name || `Tuần ${selectedWeek + 1}`}</span>
            <span className="text-slate-400 text-sm ml-2">/ {weeks.length}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setSelectedWeek(Math.min(weeks.length - 1, selectedWeek + 1))} disabled={selectedWeek >= weeks.length - 1} data-testid="next-week-btn">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Week summary */}
        {currentWeek && (
          <div className="flex items-center justify-center gap-3 mb-4 text-sm flex-wrap">
            <span className="text-slate-500">Tổng:</span>
            <span className="font-bold text-slate-800">{currentWeek.total_training_week_time}</span>
            <Badge className="bg-sky-100 text-sky-700 text-xs"><Waves className="w-3 h-3 mr-1" />{currentWeek.total_week_swim_time}</Badge>
            <Badge className="bg-emerald-100 text-emerald-700 text-xs"><Bike className="w-3 h-3 mr-1" />{currentWeek.total_week_bike_time}</Badge>
            <Badge className="bg-red-100 text-red-700 text-xs"><Footprints className="w-3 h-3 mr-1" />{currentWeek.total_week_run_time}</Badge>
          </div>
        )}

        {/* Calendar - Mobile: vertical, Desktop: grid */}
        <>
          <div className="md:hidden space-y-3" data-testid="mobile-calendar">
            {DAYS_ORDER.map((day, i) => (
              <div key={day} className="animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                <DayColumn dayName={day} exercises={exercisesByDay[day] || []} onExerciseClick={handleExerciseClick} />
              </div>
            ))}
          </div>
          <div className="hidden md:grid grid-cols-7 gap-2" data-testid="desktop-calendar">
            {DAYS_ORDER.map((day) => (
              <DayColumn key={day} dayName={day} exercises={exercisesByDay[day] || []} onExerciseClick={handleExerciseClick} />
            ))}
          </div>
        </>

        {/* Volume Chart Toggle */}
        <div className="mt-6 no-print">
          <Button variant="ghost" className="w-full flex items-center justify-between py-3" onClick={() => setShowChart(!showChart)} data-testid="toggle-chart-btn">
            <span className="flex items-center gap-2 font-bold uppercase text-sm">
              <BarChart3 className="w-4 h-4 text-orange-500" /> Biểu đồ volume
            </span>
            {showChart ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
          {showChart && (
            <Card className="mt-2">
              <CardContent className="pt-4">
                <VolumeChart weeks={chartWeeks} />
              </CardContent>
            </Card>
          )}
        </div>

        {/* Plan totals */}
        {plan.total_training_plan_time && (
          <div className="mt-6 bg-white rounded-xl p-4 border border-slate-100 shadow-sm no-print">
            <h3 className="text-base font-bold uppercase tracking-tight mb-3">Tổng kết plan</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div className="bg-slate-50 p-3 rounded-lg text-center">
                <div className="text-slate-500 text-xs">Tổng thời gian</div>
                <div className="font-bold text-lg">{plan.total_training_plan_time}</div>
              </div>
              <div className="bg-sky-50 p-3 rounded-lg text-center">
                <div className="text-sky-600 text-xs flex items-center justify-center gap-1"><Waves className="w-3 h-3" /> Bơi</div>
                <div className="font-bold text-lg text-sky-700">{plan.total_swim_time}</div>
              </div>
              <div className="bg-emerald-50 p-3 rounded-lg text-center">
                <div className="text-emerald-600 text-xs flex items-center justify-center gap-1"><Bike className="w-3 h-3" /> Đạp</div>
                <div className="font-bold text-lg text-emerald-700">{plan.total_bike_time}</div>
              </div>
              <div className="bg-red-50 p-3 rounded-lg text-center">
                <div className="text-red-600 text-xs flex items-center justify-center gap-1"><Footprints className="w-3 h-3" /> Chạy</div>
                <div className="font-bold text-lg text-red-700">{plan.total_run_time}</div>
              </div>
            </div>
            <div className="text-center mt-3 text-slate-500 text-sm">
              Tổng: <strong>{plan.total_exercise}</strong> bài tập
            </div>
          </div>
        )}

        {/* Print view */}
        <div className="print-only mt-8">
          <h2 className="text-xl font-black uppercase mb-4">{plan.training_plan_name} – {config.training_duration} tuần</h2>
          {weeks.map((week, wi) => (
            <div key={wi} className={`mb-6 ${wi > 0 ? "print-break" : ""}`}>
              <h3 className="text-base font-bold border-b pb-1 mb-2">
                {week.week_name} – {week.total_training_week_time}
              </h3>
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr>
                    {DAYS_ORDER.map((d) => (
                      <th key={d} className="border p-1 bg-slate-100 font-bold">{DAY_LABELS[d]}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {DAYS_ORDER.map((day) => {
                      const exs = (week.exercises || []).filter(e => e.exercise_date === day);
                      return (
                        <td key={day} className="border p-1 align-top" style={{ width: "14.28%" }}>
                          {exs.map((ex, ei) => (
                            <div key={ei} className="mb-1">
                              <strong>{ex.exercise_sport_type?.toUpperCase()}</strong>: {ex.exercise_name} ({ex.exercise_time})
                            </div>
                          ))}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          ))}
        </div>

        <div className="h-20" />
      </main>

      {/* Exercise detail sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto rounded-t-2xl px-4 sm:px-6">
          {selectedExercise && (
            <>
              <SheetHeader>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold ${
                    SPORT_COLORS[selectedExercise.exercise_sport_type]?.badge || "bg-slate-100 text-slate-700"
                  }`}>
                    <SportIcon sport={selectedExercise.exercise_sport_type} />
                    {{ swim: "Bơi", bike: "Đạp xe", run: "Chạy", strength: "Bổ trợ" }[selectedExercise.exercise_sport_type] || selectedExercise.exercise_sport_type}
                  </span>
                  {selectedExercise.exercise_label && (
                    <Badge variant="outline">{selectedExercise.exercise_label}</Badge>
                  )}
                  {selectedExercise.exercise_pack && (
                    <Badge variant="secondary" className="text-xs">{selectedExercise.exercise_pack}</Badge>
                  )}
                </div>
                <SheetTitle className="text-xl font-bold text-left">
                  {selectedExercise.exercise_name}
                </SheetTitle>
              </SheetHeader>

              <div className="mt-4 space-y-4">
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1 text-slate-600">
                    <Clock className="w-4 h-4" />
                    <span>{selectedExercise.exercise_time}</span>
                  </div>
                  <span className="text-slate-400">|</span>
                  <span className="text-slate-500">{selectedExercise.exercise_date}</span>
                </div>

                {selectedExercise.exercise_description && (
                  <div>
                    <h4 className="text-xs font-bold text-slate-500 uppercase mb-1">Mô tả chi tiết</h4>
                    <div className="bg-slate-900 text-orange-300 p-3 rounded-lg font-mono text-sm break-all overflow-x-auto max-w-full">
                      <span className="whitespace-pre-wrap break-words">{selectedExercise.exercise_description}</span>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function _parseTime(timeStr) {
  try {
    const parts = (timeStr || "").split(":");
    if (parts.length === 3) return parseInt(parts[0]) * 60 + parseInt(parts[1]);
    if (parts.length === 2) return parseInt(parts[0]) * 60 + parseInt(parts[1]);
    return 0;
  } catch { return 0; }
}
